import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { TripProvider, useTrip } from '../../contexts/trip-context';
import {
  AISearchCacheProvider,
  YouTubeSearchCacheProvider,
  ViatorSearchCacheProvider
} from '../../contexts/cache-instances';
import { PaginationProvider } from '../../contexts/pagination-context';
import { SortingProvider } from '../../contexts/sorting-context';
import { TripModalProvider } from '../../contexts/modal-context-trip';
import { ViewSettingsProvider } from '../../contexts/view-settings-context';
import TripOpenViewNavbar from '../../navbars/trip-open-view-navbar';
import TripModalWrapper from '../../modals/trip-modal-wrapper';
import PullToRefreshIndicator from '../../shared/pull-to-refresh-indicator';
import ErrorBoundary from '../../shared/error-boundary';
import { usePullToRefresh } from '../../../hooks/use-pull-to-refresh';
import tripsService from '../../../services/trips/trips.service';
import tripActivityService from '../../../services/trips/trip-activity.service';
import { ITrip } from '../../../Models/trips/trip.model';
import {
  getLastSectionStorageKey,
  getLastTripSection
} from '../../../utils/trip-view-state';
import './trip-open-view-page.scss';

const TripOpenViewContent: React.FC = () => {
  const { id, section } = useParams<{ id: string; section?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { trip, setTrip, refreshTrip, setRefreshTrip } = useTrip();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTrip = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const [tripData, activitiesData] = await Promise.all([
        tripsService.getTripById(id),
        tripActivityService.getActivities(id).catch(() => null)
      ]);
      setTrip(
        activitiesData != null
          ? { ...tripData, activities: activitiesData }
          : tripData
      );
    } catch (err) {
      console.error('Failed to fetch trip:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id, setTrip]);

  // Pull-to-refresh on mobile
  const pullToRefresh = usePullToRefresh({
    onRefresh: async () => {
      await fetchTrip();
    },
    threshold: 80,
    enabled: true
  });

  useEffect(() => {
    fetchTrip();
  }, [fetchTrip]);

  useEffect(() => {
    if (refreshTrip) {
      fetchTrip();
      setRefreshTrip(false);
    }
  }, [refreshTrip, fetchTrip, setRefreshTrip]);

  // Persist the active section per trip so refresh/open can restore context.
  useEffect(() => {
    if (!id || !section) return;
    localStorage.setItem(getLastSectionStorageKey(id), section);
  }, [id, section]);

  // Redirect to the last viewed section (fallback discovery) when no section is provided.
  useEffect(() => {
    if (id && !section) {
      const targetSection = getLastTripSection(id);
      navigate(`/app/trip/${id}/view/${targetSection}`, { replace: true });
    }
  }, [id, section, navigate]);

  // Always scroll to top when switching between trip view sections.
  useEffect(() => {
    const contentEl = document.querySelector(
      '.trip-open-view-content'
    ) as HTMLElement | null;
    if (contentEl) {
      contentEl.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  if (loading) {
    return (
      <div className='trip-open-view-loading'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className='trip-open-view-error'>
        <h3>Failed to load trip</h3>
        <p>{error?.message || 'Unknown error'}</p>
        <button
          className='btn btn-primary'
          onClick={fetchTrip}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      ref={pullToRefresh.containerRef}
      className='trip-open-view-page'
    >
      <PullToRefreshIndicator
        pullDistance={pullToRefresh.pullDistance}
        threshold={80}
        refreshing={pullToRefresh.refreshing}
        shouldRefresh={pullToRefresh.shouldRefresh}
      />
      <div className='container-px'>
        <TripOpenViewNavbar
          tripTitle={trip.title}
          activeSection={section}
        />
        <div className='trip-open-view-content'>
          <Outlet />
        </div>
      </div>
      <TripModalWrapper />
    </div>
  );
};

const TripOpenViewPage: React.FC = () => {
  return (
    <ErrorBoundary context='Trip View'>
      <TripProvider>
        <AISearchCacheProvider>
          <YouTubeSearchCacheProvider>
            <ViatorSearchCacheProvider>
              <PaginationProvider>
                <SortingProvider>
                  <TripModalProvider>
                    <ViewSettingsProvider>
                      <TripOpenViewContent />
                    </ViewSettingsProvider>
                  </TripModalProvider>
                </SortingProvider>
              </PaginationProvider>
            </ViatorSearchCacheProvider>
          </YouTubeSearchCacheProvider>
        </AISearchCacheProvider>
      </TripProvider>
    </ErrorBoundary>
  );
};

export default TripOpenViewPage;
